import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton as DeleteIconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface HeaderConfigDialogProps {
  open: boolean;
  headerConfig?: string;
  onClose: () => void;
  onSave?: (headerConfig: string) => void;
}

export const HeaderConfigDialog: React.FC<HeaderConfigDialogProps> = ({
  open,
  headerConfig,
  onClose,
  onSave
}) => {
  const [editedConfig, setEditedConfig] = useState<any>(null);

  useEffect(() => {
    if (open && headerConfig) {
      try {
        const parsed = JSON.parse(headerConfig);
        setEditedConfig(JSON.parse(JSON.stringify(parsed))); // Deep copy
      } catch (error) {
        console.error('Failed to parse header config:', error);
        setEditedConfig({
          type: 'dynamic',
          height: 56,
          backgroundColor: '#c0c0c0',
          rightPadding: 15,
          elements: []
        });
      }
    }
  }, [open, headerConfig]);

  const handleSave = () => {
    if (editedConfig && onSave) {
      try {
        const jsonString = JSON.stringify(editedConfig, null, 2);
        onSave(jsonString);
        onClose();
      } catch (error) {
        console.error('Failed to save header config:', error);
      }
    }
  };

  const updateConfig = (path: string, value: any) => {
    setEditedConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newConfig;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const addElement = () => {
    setEditedConfig((prev: any) => ({
      ...prev,
      elements: [
        ...prev.elements,
        {
          type: 'text',
          content: 'New Text',
          fontSize: 12,
          fontWeight: 'normal',
          color: '#000000',
          fontFamily: 'Arial',
          x: 10,
          y: 10,
          alignment: 'left'
        }
      ]
    }));
  };

  const updateElement = (index: number, field: string, value: any) => {
    setEditedConfig((prev: any) => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig.elements[index][field] = value;
      return newConfig;
    });
  };

  const removeElement = (index: number) => {
    setEditedConfig((prev: any) => ({
      ...prev,
      elements: prev.elements.filter((_: any, i: number) => i !== index)
    }));
  };

  if (!editedConfig) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: '80vh' }
      }}
    >
      <DialogTitle>
        Edit Header Configuration
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          {/* Header Properties */}
          <Typography variant="h6" gutterBottom>
            Header Properties
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Height"
                type="number"
                value={editedConfig.height || 56}
                onChange={(e) => updateConfig('height', parseInt(e.target.value) || 56)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Background Color"
                value={editedConfig.backgroundColor || '#c0c0c0'}
                onChange={(e) => updateConfig('backgroundColor', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Right Padding"
                type="number"
                value={editedConfig.rightPadding || 15}
                onChange={(e) => updateConfig('rightPadding', parseInt(e.target.value) || 15)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Elements */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Text Elements
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addElement}
            >
              Add Element
            </Button>
          </Box>

          {editedConfig.elements?.map((element: any, index: number) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Element {index + 1}
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => removeElement(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Content"
                      value={element.content || ''}
                      onChange={(e) => updateElement(index, 'content', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Font Size"
                      type="number"
                      value={element.fontSize || 12}
                      onChange={(e) => updateElement(index, 'fontSize', parseInt(e.target.value) || 12)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Font Weight</InputLabel>
                      <Select
                        value={element.fontWeight || 'normal'}
                        label="Font Weight"
                        onChange={(e) => updateElement(index, 'fontWeight', e.target.value)}
                      >
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="bold">Bold</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Color"
                      value={element.color || '#000000'}
                      onChange={(e) => updateElement(index, 'color', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Font Family"
                      value={element.fontFamily || 'Arial'}
                      onChange={(e) => updateElement(index, 'fontFamily', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="X Position"
                      value={element.x || 10}
                      onChange={(e) => updateElement(index, 'x', e.target.value === 'right' ? 'right' : parseInt(e.target.value) || 10)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Y Position"
                      type="number"
                      value={element.y || 10}
                      onChange={(e) => updateElement(index, 'y', parseInt(e.target.value) || 10)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Alignment</InputLabel>
                      <Select
                        value={element.alignment || 'left'}
                        label="Alignment"
                        onChange={(e) => updateElement(index, 'alignment', e.target.value)}
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {(!editedConfig.elements || editedConfig.elements.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No text elements. Click "Add Element" to add text to the header.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
